import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  CheckCircle,
  Mail,
  Key,
  ArrowRight,
  Copy,
  Check,
  ExternalLink,
  Info,
} from "lucide-react";

const RegistrationSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [copied, setCopied] = React.useState("");

  const { tenant, admin } = location.state || {};

  useEffect(() => {
    if (!tenant) {
      navigate("/register-tenant");
    }
  }, [tenant, navigate]);

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(""), 2000);
  };

  if (!tenant) return null;

  const loginUrl = `http://${tenant.subdomain}.lawsystem.com/login`;

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 py-12"
      dir="rtl"
    >
      <div className="max-w-4xl mx-auto px-4">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            🎉 تم إنشاء حسابك بنجاح!
          </h1>
          <p className="text-xl text-gray-600">
            مرحباً بك في نظام إدارة مكاتب المحاماة
          </p>
        </div>

        {/* Account Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Login Info */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-start gap-4">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Mail className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-3">
                  معلومات الدخول
                </h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm text-gray-500">
                      البريد الإلكتروني:
                    </span>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="bg-gray-100 px-3 py-2 rounded text-sm flex-1 font-mono">
                        {admin?.email}
                      </code>
                      <button
                        onClick={() => copyToClipboard(admin?.email, "email")}
                        className="p-2 hover:bg-gray-100 rounded-lg transition"
                        title="نسخ"
                      >
                        {copied === "email" ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4 text-gray-600" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Office Link */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-start gap-4">
              <div className="bg-green-100 p-3 rounded-lg">
                <Key className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-3">
                  رابط المكتب
                </h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm text-gray-500">الرابط:</span>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="bg-gray-100 px-3 py-2 rounded text-xs flex-1 font-mono break-all">
                        {tenant.subdomain}.lawsystem.com
                      </code>
                      <button
                        onClick={() =>
                          copyToClipboard(
                            `${tenant.subdomain}.lawsystem.com`,
                            "url"
                          )
                        }
                        className="p-2 hover:bg-gray-100 rounded-lg transition flex-shrink-0"
                        title="نسخ"
                      >
                        {copied === "url" ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4 text-gray-600" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-white rounded-xl shadow-md p-8 mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">
            الخطوات التالية
          </h3>

          <div className="space-y-6">
            {/* Step 1 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-bold">1</span>
                </div>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-600" />
                  تحقق من بريدك الإلكتروني
                </h4>
                <p className="text-gray-600 text-sm">
                  أرسلنا رسالة تحتوي على معلومات حسابك وتعليمات البدء إلى{" "}
                  <strong className="text-gray-900">{admin?.email}</strong>
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-bold">2</span>
                </div>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Key className="w-5 h-5 text-green-600" />
                  سجل الدخول إلى لوحة التحكم
                </h4>
                <p className="text-gray-600 text-sm mb-3">
                  استخدم البريد الإلكتروني وكلمة المرور للدخول إلى حسابك
                </p>
                <a
                  href={loginUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-semibold"
                >
                  افتح صفحة تسجيل الدخول
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-purple-600 font-bold">3</span>
                </div>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-purple-600" />
                  ابدأ بإضافة بياناتك
                </h4>
                <p className="text-gray-600 text-sm">
                  أضف العملاء، القضايا، والمستندات وابدأ في تنظيم عمل مكتبك
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Trial Notice */}
        {tenant.status === "trial" && (
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-4">
              <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-blue-900 mb-2">
                  فترة تجريبية - 14 يوم
                </h4>
                <p className="text-blue-800 text-sm mb-3">
                  أنت الآن في الفترة التجريبية المجانية. يمكنك استخدام جميع
                  المميزات الأساسية والتعرف على النظام بشكل كامل.
                </p>
                <p className="text-blue-800 text-sm">
                  يمكنك الترقية لأي خطة مدفوعة في أي وقت من إعدادات الحساب.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Support */}
        <div className="bg-gray-50 rounded-xl p-6 mb-8">
          <h4 className="font-semibold text-gray-900 mb-3">
            هل تحتاج إلى مساعدة؟
          </h4>
          <p className="text-gray-600 text-sm mb-4">
            فريقنا جاهز لمساعدتك في أي وقت. تواصل معنا عبر:
          </p>
          <div className="flex flex-wrap gap-4 text-sm">
            <a
              href="mailto:support@lawsystem.com"
              className="text-blue-600 hover:underline"
            >
              📧 support@lawsystem.com
            </a>
            <a
              href="https://wa.me/966501234567"
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-600 hover:underline flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-whatsapp" viewBox="0 0 16 16">
                <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007-.133 0-.347.05-.529.248-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z"/>
              </svg>
              +966 50 123 4567
            </a>
            <a href="#" className="text-blue-600 hover:underline">
              💬 الدردشة المباشرة
            </a>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate("/login")}
            className="px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-2 text-lg font-semibold shadow-lg"
          >
            تسجيل الدخول الآن
            <ArrowRight className="w-5 h-5" />
          </button>
          <button
            onClick={() => navigate("/")}
            className="px-8 py-4 bg-white text-gray-700 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition text-lg font-semibold"
          >
            العودة للصفحة الرئيسية
          </button>
        </div>

        {/* Additional Info */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>
            تم إنشاء حسابك بنجاح باسم{" "}
            <strong className="text-gray-900">{tenant.name}</strong>
          </p>
          <p className="mt-2">
            معرف المكتب:{" "}
            <code className="bg-gray-100 px-2 py-1 rounded font-mono text-xs">
              {tenant.id}
            </code>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegistrationSuccess;
